import { z } from 'zod';

import {
  idRefSchema,
  isoTimestampSchema,
  ticketNumberSchema,
  uuidSchema,
} from './primitives.js';

export const ticketStatusSchema = z.enum([
  'new',
  'triaged',
  'in_progress',
  'awaiting_client',
  'resolved',
  'closed',
]);

export const ticketPrioritySchema = z.enum(['p1', 'p2', 'p3']);

export const ticketSourceSchema = z.enum([
  'email',
  'portal',
  'internal',
  'system',
]);

export const ticketSchema = z.object({
  id: uuidSchema,
  contractId: uuidSchema,
  number: ticketNumberSchema,
  subject: z.string().min(1).max(500),
  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  source: ticketSourceSchema,
  submitterUserId: uuidSchema.nullable(),
  assigneeUserId: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  firstResponseAt: isoTimestampSchema.nullable(),
  resolvedAt: isoTimestampSchema.nullable(),
  closedAt: isoTimestampSchema.nullable(),
  responseDeadlineAt: isoTimestampSchema.nullable(),
  solutionDeadlineAt: isoTimestampSchema.nullable(),
  responsePausedMs: z.number().int().nonnegative(),
  solutionPausedMs: z.number().int().nonnegative(),
  pausedAt: isoTimestampSchema.nullable(),
  automationRuleId: uuidSchema.nullable(),
  automationFireId: uuidSchema.nullable(),
});

/**
 * Legal status transitions — used to validate every status mutation.
 * Reflects the canonical state machine in SPEC.md §6 + decisions row 28:
 * - `closed` is terminal.
 * - `resolved` can be reopened by a client email reply (→ `in_progress`).
 * - `awaiting_client` only reachable from `in_progress`.
 */
const TRANSITIONS: Record<
  z.infer<typeof ticketStatusSchema>,
  ReadonlyArray<z.infer<typeof ticketStatusSchema>>
> = {
  new: ['triaged', 'in_progress', 'closed'],
  triaged: ['in_progress', 'closed'],
  in_progress: ['awaiting_client', 'resolved', 'closed'],
  awaiting_client: ['in_progress', 'resolved', 'closed'],
  resolved: ['in_progress', 'closed'],
  closed: [],
};

export function isLegalStatusTransition(
  from: z.infer<typeof ticketStatusSchema>,
  to: z.infer<typeof ticketStatusSchema>
): boolean {
  return TRANSITIONS[from].includes(to);
}

// idRefSchema imported from primitives — accepts UUIDv7 + seed kebab IDs.

export const ticketCreateInputSchema = z.object({
  contractId: idRefSchema,
  subject: z.string().min(1).max(500),
  priority: ticketPrioritySchema,
  source: ticketSourceSchema,
  submitterUserId: idRefSchema.nullable(),
  bodyText: z.string().min(1),
  bodyHtml: z.string().nullable(),
});

export type TicketCreateInput = z.infer<typeof ticketCreateInputSchema>;

export const ticketStatusUpdateInputSchema = z.object({
  status: ticketStatusSchema,
});

export const ticketAssignInputSchema = z.object({
  assigneeUserId: idRefSchema.nullable(),
});

export const ticketPriorityUpdateInputSchema = z.object({
  priority: ticketPrioritySchema,
});
